import React from 'react'
import { presenterHub, presenterUser } from '@RestApi/types'
import styled, { css } from 'styled-components'
import Avatar from '@Components/Avatar'
import media from '@Styles/media'
import { HubFormattedMessage } from 'src/common/rtl/HubTextFormatting'
import { HubParagraph } from 'src/common/rtl/HubWidgets'
import { IntlShape } from 'react-intl/src/types'
import sharedMessages from '@Locales/shared.messages'
import { sharedMessages as commonMessages } from '@Locales/index'
import MemberId from './MemberId'
import messages from '@Modules/hubs/main/messages'
import { useAddAlert } from '@Modules/hubs/atoms'
import RoleSelect from './RoleSelect'
import { SessionResponse } from '@RestApi/client'
import Popover from '@Components/Popover'
import UserCard from './UserCard'
import { atLeastModerator } from '@App/roles/utils'
import { retrieveSignedInUserPermissions } from '@Helpers/hubs/retrieveUserPermissions'

type Props = {
  user: presenterUser
  hub: presenterHub
  intl: IntlShape
  session: SessionResponse
  className: string
  itemProp: string
}

const User: React.FC<Props> = (props: Props) => {
  const { session, user, hub, intl, className, itemProp } = props
  const { memberId, role, name } = user
  const addAlert = useAddAlert()
  const userPermissions = retrieveSignedInUserPermissions(hub)

  const isNameEmpty = (user: presenterUser): boolean => {
    return !(user && /\S/.test(name))
  }

  const handleCopyMemberId = () => {
    addAlert({
      message: intl.formatMessage(messages.memberIdCopy),
      type: 'SUCCESS',
    })
  }

  const renderName = (canOpenPopup = false) => {
    return (
      <Name canOpenPopup={canOpenPopup}>
        <span itemProp="name">
          {isNameEmpty(user) ? (
            <HubFormattedMessage {...sharedMessages.anonymous} />
          ) : (
            <HubParagraph>{name}</HubParagraph>
          )}
        </span>
      </Name>
    )
  }

  return (
    <UserContainer
      className={className}
      itemProp={itemProp}
      itemScope
      itemType="http://schema.org/OrganizationRole"
    >
      <meta itemProp="roleName" content={role} />
      <AvatarContainer>
        <Avatar type="user" size={90} account={user} />
      </AvatarContainer>

      <Details itemProp="member" itemScope itemType="http://schema.org/Person">
        <meta itemProp="url" content={`${hub.hub_url}`} />
        {userPermissions.canOpenPopup ? (
          <Popover
            position={['bottom']}
            align="start"
            target={renderName(userPermissions.canOpenPopup)}
            reposition={false}
            content={(position: any) => {
              // position.nudgedTop has a non-zero value only if the popover is out of the viewport
              return position.nudgedTop == 0 ? (
                <UserCard
                  user={user}
                  hub={hub}
                  displayMemberIdAndEmail={userPermissions.canSeeMemberIdsAndEmails}
                />
              ) : null
            }}
          />
        ) : (
          renderName()
        )}
        {memberId &&
          (userPermissions.canSeeMemberIdsAndEmails || atLeastModerator({ role: hub.role })) && (
            <MemberId handleCopyMemberId={handleCopyMemberId} intl={intl} memberId={memberId} />
          )}

        {atLeastModerator({ role: hub.role }) ? (
          <RoleSelect user={user} hub={hub} session={session} intl={intl} />
        ) : user.role ? (
          <Role>
            {/* @ts-ignore */}
            <HubFormattedMessage {...commonMessages[user.role]} />
          </Role>
        ) : (
          ''
        )}
      </Details>
    </UserContainer>
  )
}

const AvatarContainer = styled.div`
  .px-avatar,
  .imageContainer,
  .image {
    ${media.smallerThanLargeScreen`
      width: 56px !important;
      height: 56px !important;
      min-height: 56px !important;
      font-size: 22px !important;
    `};
  }
`

const UserContainer = styled.div`
  width: 100%;
  height: auto;
  font-size: 12px;
  border-bottom: 1px solid lightgray;
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.sizes.gutterLarge} 0px;

  > div {
    margin: 0 ${({ theme }) => theme.sizes.gutterLarge};

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }

    ${media.smallScreen`
      margin: 0 ${({ theme }: any) => theme.sizes.gutterSmall};
    `};
    ${media.smallerThanLargeScreen`
      margin: 0 ${({ theme }: any) => theme.sizes.gutterSmall};
    `};
  }
`

const Details = styled.div`
  line-height: 1.5;
  font-weight: 300;
`

const Name = styled.div<{ canOpenPopup: boolean }>`
  word-break: break-word;
  font-size: ${({ theme }) => theme.fontSizes.fontSizeLarge};
  color: inherit;

  ${({ canOpenPopup }) =>
    css`
      cursor: ${canOpenPopup && 'pointer'};
    `}

  ${media.mediumScreen`
    font-size: ${({ theme }: any) => theme.fontSizes.fontSizeNormal};
  `};
  ${media.smallScreen`
    font-size: ${({ theme }: any) => theme.fontSizes.fontSizeSmall};
  `};
`

const Role = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.fontSizeSmall};
`

export default User
